import React from "react";
import { Link } from "react-router-dom";
import { Calendar, MapPin, Users, Sparkles } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { mockData } from "../utils/mockData";

const EventsSection = () => {
  const events = mockData.events;

  const getEventIcon = (type) => {
    switch (type) {
      case 'launch':
        return Sparkles;
      case 'ski':
        return Users;
      default:
        return Calendar;
    }
  };

  return (
    <section className="py-20 px-6 bg-white/60 backdrop-blur-sm">
      <div className="max-w-6xl mx-auto">
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {events.map((event, index) => {
            const IconComponent = getEventIcon(event.type);
            return (
              <Card key={index} className="group hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-3 border-blue-100/50 overflow-hidden bg-white/90 backdrop-blur-sm relative flex flex-col h-full">
                <div className="h-2" style={{background: 'linear-gradient(to right, #1d476a, rgb(51 65 85))'}}></div>
                
                {/* Winter Wonderland Banner - Top Right Corner */}
                {event.name.includes("Winter Wonderland") && (
                  <div className="absolute top-6 right-3 z-10">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_codebase-navigator-1/artifacts/mv5j1i4a_Winter%20Wonderland%20Flyer.PNG"
                      alt="Winter Wonderland Banner"
                      className="winter-wonderland-poster object-cover rounded-lg shadow-lg opacity-90 hover:opacity-100 transition-all duration-300 cursor-pointer hover:scale-105"
                      style={{objectPosition: 'center top', backgroundColor: 'transparent'}}
                      onClick={() => window.open('https://posh.vip/e/winter-wonderland-1', '_blank')}
                    />
                  </div>
                )}

                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 shadow-lg" style={{background: 'linear-gradient(to bottom right, rgba(29, 71, 106, 0.1), rgba(148, 163, 184, 0.1))'}}>
                        <IconComponent className="w-6 h-6 transition-colors duration-300" style={{color: '#1d476a'}} />
                      </div>
                      <Badge 
                        variant="secondary" 
                        className="border-green-200" 
                        style={{
                          backgroundColor: event.status === 'On Sale' ? '#E5F6DF' : 'rgba(29, 71, 106, 0.05)', 
                          color: event.status === 'On Sale' ? '#22c55e' : '#1d476a'
                        }}
                      >
                        {event.status}
                      </Badge>
                    </div>
                  </div>
                  <CardTitle className="text-2xl text-slate-800 transition-colors duration-300">
                    {event.name.includes('\n') ? (
                      event.name.split('\n').map((line, index) => (
                        <div key={index}>{line}</div>
                      ))
                    ) : (
                      event.name
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 flex-grow flex flex-col">
                  <div className="flex items-center space-x-2 text-slate-600">
                    <Calendar className="w-5 h-5" />
                    <span>{event.date}</span>
                  </div>
                  {event.location && (
                    <div className="flex items-center space-x-2 text-slate-600">
                      <MapPin className="w-5 h-5" />
                      <span>{event.location}</span>
                    </div>
                  )}
                  <p className="text-slate-700 leading-relaxed flex-grow">
                    {event.description}
                  </p>
                  {event.name === 'Slope Social I: All-In Ski Weekend' ? (
                    <Link to="/slope-social-i">
                      <Button 
                        className="w-full mt-6 transition-all duration-300 transform hover:scale-105 shadow-lg text-white"
                        style={{
                          background: 'linear-gradient(to right, #1d476a, rgb(51 65 85))',
                          border: 'none'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = 'linear-gradient(to right, #163d56, rgb(45 55 72))';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'linear-gradient(to right, #1d476a, rgb(51 65 85))';
                        }}
                      >
                        View Details
                      </Button>
                    </Link>
                  ) : event.name === 'Winter Wonderland: Slope Social Kickoff' ? (
                    <Button 
                      className="w-full mt-6 transition-all duration-300 transform hover:scale-105 shadow-lg text-white"
                      style={{
                        background: 'linear-gradient(to right, #1d476a, rgb(51 65 85))',
                        border: 'none'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = 'linear-gradient(to right, #163d56, rgb(45 55 72))';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'linear-gradient(to right, #1d476a, rgb(51 65 85))';
                      }}
                      onClick={() => window.open('https://posh.vip/e/winter-wonderland-1', '_blank')}
                    >
                      Get Tickets
                    </Button>
                  ) : (
                    <Button 
                      className={`w-full mt-6 transition-all duration-300 transform hover:scale-105 ${
                        event.status === 'Coming Soon' 
                          ? 'bg-slate-400 hover:bg-slate-500 cursor-not-allowed' 
                          : 'shadow-lg text-white'
                      }`}
                      style={{
                        background: event.status === 'Coming Soon' 
                          ? undefined 
                          : 'linear-gradient(to right, #1d476a, rgb(51 65 85))',
                        border: 'none'
                      }}
                      disabled={event.status === 'Coming Soon'}
                      onMouseEnter={(e) => {
                        if (event.status !== 'Coming Soon') {
                          e.target.style.background = 'linear-gradient(to right, #163d56, rgb(45 55 72))';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (event.status !== 'Coming Soon') {
                          e.target.style.background = 'linear-gradient(to right, #1d476a, rgb(51 65 85))';
                        }
                      }}
                    >
                      {event.status === 'Coming Soon' ? 'Stay Tuned' : 'Get Notified'}
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default EventsSection;